import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Flex, Spin } from "antd";

import { authCheck } from "@av/apps/common-apps/platform/services/authentication/authentication";
import { pathWithReturn } from "@av/pages/auth/utils/return-to";

import type { AxiosError } from "axios";

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

function RequireAuth({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const [isLoggedIn, setLoggedIn] = useState<boolean | null>(null);

  const initLogin = useCallback(
    async (ac: AbortController) => {
      try {
        await authCheck(ac);

        setLoggedIn(true);
      } catch (e: unknown) {
        const error = e as AxiosError;

        // Cancel means the request was aborted with the abort controller
        if (error.code !== "ERR_CANCELED") {
          console.error("Auth request failed", error);

          setLoggedIn(false);
        }
      }
    },
    [setLoggedIn]
  );

  useEffect(() => {
    const controller = new AbortController();

    initLogin(controller);

    return () => controller.abort();
  }, [initLogin]);

  if (isLoggedIn === null) {
    return (
      <Flex justify="center" align="center" style={{ height: "100vh" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to={pathWithReturn("/login", location)} replace />;
  }

  return children ? children : <Outlet />;
}

export { RequireAuth };
